import { parseCalendarDate } from '@/lib/date-time-utils';

/** The two fields PB-5710 added to PermitRateOutput, as this flow consumes them. */
export interface UpcomingRateLike {
  upcoming?: boolean;
  startDate?: string;
}

/**
 * Whether a product is flagged as not yet effective.
 *
 * ## Invariants:
 * - Requires BOTH the flag and the date. `upcoming` without a `startDate` can be neither
 *   rendered ("Available from " with no date) nor bumped to, so it is treated as ordinary.
 * - This governs the BADGE only. It does NOT decide whether the booking date moves — see
 *   `resolveUpcomingBump`, which additionally requires the start to be in the shopper's
 *   future.
 */
export const isUpcomingRate = (rate?: UpcomingRateLike): boolean =>
  !!rate && rate.upcoming === true && !!rate.startDate;

export interface UpcomingBumpResult {
  /** The date the booking start-date field should hold. */
  date: Date;
  /** The yyyy-MM-dd driving the notice; undefined when no bump applies. */
  noticeDate?: string;
}

/**
 * Resolves the effective booking start date from the shopper's own date and the product
 * they have selected. PURE and DERIVED — it holds no state and is safe to call on every
 * render.
 *
 * ## Invariants:
 * - The date moves only when the selected product's effective start is strictly AFTER the
 *   date the shopper chose. The `upcoming` flag alone is NOT sufficient: the backend computes
 *   it against the date the rates were fetched for, and the list refetches only after the
 *   shopper's date changes (PB-3319), so for that window the flag reflects the previous date.
 *   A shopper who already chose a date beyond the product's start would otherwise have their
 *   date dragged BACKWARDS.
 * - Being a derivation rather than an event handler is the point. An earlier cut applied the
 *   bump inside the rate-click handler only, which left every OTHER route into "a product is
 *   selected" unguarded — the load-time auto-select picked an upcoming product without ever
 *   moving the date, and an edit to an earlier date silently left the permit starting before
 *   the product existed. Deriving it means auto-select, click and date-edit all reconcile
 *   through this one function and cannot disagree.
 * - The shopper's own date is never overwritten, so selecting an ordinary product afterwards
 *   restores it with no bookkeeping: the bump simply stops applying.
 * - When nothing is selected, or the product is ordinary, the shopper's date passes through
 *   untouched. That is the path every existing company takes, because the company toggle is
 *   off and the API therefore never returns `upcoming`.
 */
export const resolveUpcomingBump = (
  rate: UpcomingRateLike | undefined,
  shopperDate: Date
): UpcomingBumpResult => {
  if (isUpcomingRate(rate)) {
    const start = parseCalendarDate(rate!.startDate!);
    if (start.getTime() > shopperDate.getTime()) {
      return { date: start, noticeDate: rate!.startDate };
    }
  }
  return { date: shopperDate };
};

/**
 * The booking picker's upper bound must be able to represent a bumped date.
 *
 * ## Invariants:
 * - Returns the later of the configured cap and the date a bump actually justifies. A no-op
 *   whenever `allowedFutureDateParking` exceeds the upcoming window, which is the normal
 *   configuration, so it does not widen the range operators chose.
 * - It exists because the cap falls back to today + 1 day when the theme value is absent, at
 *   which point a product a fortnight out sits outside the picker's own `maxDate`: the field
 *   would show a date the picker declares unselectable.
 * - Callers floor this on the DERIVED booking date, which may be either the bumped date or
 *   the shopper's own pick. An earlier revision of this docblock said to floor only on a
 *   live bump; that was wrong about the tradeoff and disagreed with the one call site.
 *   Flooring only on a live bump means that after the shopper edits the date inside a
 *   widened window and then selects an ordinary product, the field holds a date the picker
 *   declares unselectable — the very failure this helper exists to prevent. Flooring on the
 *   derived value keeps the field representable at every step; the ceiling can never exceed
 *   a date the shopper themselves selected, and it drops back to the configured cap as soon
 *   as the held date is within it.
 * - NOTE this means the picker's ceiling can sit above `allowedFutureDateParking` after such
 *   an edit. That cap is NOT enforced anywhere in this flow and never has been:
 *   `onBookingStartDateChange` accepts any date, and a pre-existing PB-3465 test drives it
 *   with a date months beyond the cap and asserts that date submits. Enforcing the cap would
 *   be a deliberate behaviour change to that path, not a fix to this one, and belongs in its
 *   own ticket.
 */
export const widenMaxForBump = (cap: Date, bumpedTo?: Date): Date =>
  bumpedTo && bumpedTo.getTime() > cap.getTime() ? bumpedTo : cap;

/**
 * Picks the product auto-selected when the list first loads.
 *
 * ## Invariants:
 * - Prefers the cheapest product available on the date the shopper holds, so the load-time
 *   selection does not move their start date before they have chosen anything.
 * - Falls back to the cheapest overall when every product is upcoming, preserving the
 *   pre-change behaviour of always having a selection. NOTE the date still moves in that
 *   case — `resolveUpcomingBump` derives from whatever is selected, including this fallback,
 *   so the shopper sees the bumped date and the notice rather than a silently ineligible
 *   combination.
 * - Ordering is by ascending rate, unchanged.
 */
export const pickDefaultRateOption = <T extends UpcomingRateLike & { rate: number }>(
  options: T[],
  currentDate: Date
): T | undefined => {
  if (options.length === 0) return undefined;
  const availableNow = options.filter((o) => {
    if (!isUpcomingRate(o)) return true;
    return parseCalendarDate(o.startDate!).getTime() <= currentDate.getTime();
  });
  const pickFrom = availableNow.length > 0 ? availableNow : options;
  return pickFrom.reduce((min, o) => (o.rate < min.rate ? o : min));
};
